/**
 * Gerenciamento de consentimento de cookies (LGPD).
 * Categorias: necessários (sempre ativos), analíticos e marketing.
 * O estado é sincronizado com o Google Consent Mode v2 e com o dataLayer,
 * de forma que GA4, Google Ads, GTM e Meta Pixel só disparem quando permitido.
 */

export type ConsentState = {
  analytics: boolean;
  marketing: boolean;
  updatedAt: string;
  version: number;
};

export const CONSENT_VERSION = 1;
export const CONSENT_STORAGE_KEY = "nook.cookie-consent";
export const CONSENT_EVENT = "nook:cookie-consent-change";
export const OPEN_PREFERENCES_EVENT = "nook:open-cookie-preferences";

export function readConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveConsent(input: { analytics: boolean; marketing: boolean }): ConsentState {
  const state: ConsentState = {
    analytics: input.analytics,
    marketing: input.marketing,
    updatedAt: new Date().toISOString(),
    version: CONSENT_VERSION,
  };

  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* storage indisponível não deve quebrar a página */
    }
    applyConsent(state);
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: state }));
  }

  return state;
}

export function applyConsent(state: ConsentState) {
  if (typeof window === "undefined") return;
  const granted = (allowed: boolean) => (allowed ? "granted" : "denied");
  try {
    window.dataLayer = window.dataLayer || [];
    window.gtag?.("consent", "update", {
      analytics_storage: granted(state.analytics),
      ad_storage: granted(state.marketing),
      ad_user_data: granted(state.marketing),
      ad_personalization: granted(state.marketing),
    });
    window.dataLayer.push({
      event: "cookie_consent_update",
      consent_analytics: state.analytics,
      consent_marketing: state.marketing,
    });
    window.fbq?.("consent", state.marketing ? "grant" : "revoke");
  } catch {
    /* nunca quebrar a página por causa de consentimento */
  }
}

export function openCookiePreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES_EVENT));
}
